import React, { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../../hooks/useApi';
import logger from '../../../utils/logger';
import './AdminMarketing.css';

const SERVICES = [
    {
        key: 'ga4',
        title: 'Google Analytics 4',
        cabinet: 'https://analytics.google.com/',
        hint: 'Measurement ID вида G-XXXXXXXXXX. Скрипт грузится только после согласия на аналитические cookie.',
    },
    {
        key: 'metrika',
        title: 'Яндекс.Метрика',
        cabinet: 'https://metrika.yandex.ru/',
        hint: 'Номер счётчика (только цифры). Вебвизор включается отдельно — он пишет действия пользователей.',
    },
    {
        key: 'gsc',
        title: 'Google Search Console',
        cabinet: 'https://search.google.com/search-console',
        hint: 'Подтверждение прав через HTML-файл: добавьте имя файла и его содержимое ниже.',
    },
    {
        key: 'webmaster',
        title: 'Яндекс.Вебмастер',
        cabinet: 'https://webmaster.yandex.ru/',
        hint: 'Так же через HTML-файл: yandex_xxxx.html и его содержимое.',
    },
];

function AdminMarketingIntegrations() {
    const { request } = useApi();
    const [data, setData] = useState(null);
    const [ga4, setGa4] = useState('');
    const [metrika, setMetrika] = useState('');
    const [webvisor, setWebvisor] = useState(false);
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const payload = await request('/api/v1/admin/marketing/integrations');
            setData(payload);
            const analytics = payload?.analytics || {};
            setGa4(analytics.ga4_measurement_id || '');
            setMetrika(analytics.yandex_metrika_id || '');
            setWebvisor(Boolean(analytics.yandex_metrika_webvisor));
            setFiles(analytics.verification_files || []);
        } catch (error) {
            logger.error('Не удалось загрузить интеграции', error);
            setMessage({ type: 'error', text: 'Не удалось загрузить настройки' });
        } finally {
            setLoading(false);
        }
    }, [request]);

    useEffect(() => {
        load();
    }, [load]);

    const save = async () => {
        setLoading(true);
        setMessage(null);
        try {
            const payload = {
                ga4_measurement_id: ga4.trim(),
                yandex_metrika_id: metrika.trim(),
                yandex_metrika_webvisor: webvisor,
                verification_files: files.filter((item) => item.filename?.trim() && item.content?.trim()),
            };
            await request('/api/v1/admin/marketing/integrations', {
                method: 'PUT',
                body: JSON.stringify(payload),
            });
            setMessage({ type: 'ok', text: 'Сохранено. Счётчики подхватятся при следующей загрузке страницы сайта.' });
            await load();
        } catch (error) {
            logger.error('Не удалось сохранить интеграции', error);
            setMessage({ type: 'error', text: error?.message || 'Не удалось сохранить' });
        } finally {
            setLoading(false);
        }
    };

    const updateFile = (index, field, value) => {
        setFiles((prev) => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
    };

    const addFile = () => setFiles((prev) => [...prev, { filename: '', content: '' }]);
    const removeFile = (index) => setFiles((prev) => prev.filter((_, i) => i !== index));

    const statusOf = (key) => {
        if (key === 'ga4') return Boolean(ga4.trim());
        if (key === 'metrika') return Boolean(metrika.trim());
        if (key === 'gsc') return files.some((item) => (item.filename || '').startsWith('google'));
        if (key === 'webmaster') return files.some((item) => (item.filename || '').startsWith('yandex'));
        return false;
    };

    return (
        <div className="mkt-page">
            <h1>Интеграции аналитики</h1>
            <p className="mkt-page__hint">
                ID счётчиков хранятся в системных настройках, а не в сборке фронтенда — менять их можно здесь,
                без пересборки и деплоя.
            </p>

            <div className="mkt-kpis">
                {SERVICES.map((service) => (
                    <div className="mkt-kpi" key={service.key}>
                        <b style={{ fontSize: 15 }}>{service.title}</b>
                        <span className={`mkt-status mkt-status--${statusOf(service.key) ? 'on' : 'off'}`}>
                            {statusOf(service.key) ? 'подключено' : 'не подключено'}
                        </span>
                        <div style={{ marginTop: 8 }}>
                            <a href={service.cabinet} target="_blank" rel="noopener noreferrer">Личный кабинет ↗</a>
                        </div>
                    </div>
                ))}
            </div>

            <div className="mkt-card">
                <div className="mkt-card__title">Счётчики</div>
                <div className="mkt-grid">
                    <label className="mkt-field">
                        <span>GA4 Measurement ID</span>
                        <input value={ga4} onChange={(e) => setGa4(e.target.value)} placeholder="G-XXXXXXXXXX" />
                    </label>
                    <label className="mkt-field">
                        <span>Номер счётчика Метрики</span>
                        <input value={metrika} onChange={(e) => setMetrika(e.target.value)} placeholder="12345678" />
                    </label>
                </div>
                <div className="mkt-actions">
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                        <input type="checkbox" checked={webvisor} onChange={(e) => setWebvisor(e.target.checked)} />
                        Вебвизор в Метрике
                    </label>
                </div>
            </div>

            <div className="mkt-card">
                <div className="mkt-card__title">Подтверждение прав (Search Console / Вебмастер)</div>
                <p className="mkt-page__hint" style={{ marginBottom: 12 }}>
                    Скопируйте имя файла и его содержимое из сервиса. Файл будет отдаваться с корня домена —
                    например <span className="mkt-code">{data?.public_url}/googleabc123.html</span>.
                </p>

                {files.length === 0 && <div className="mkt-empty">Файлы не добавлены</div>}

                {files.map((item, index) => (
                    <div className="mkt-grid" key={index} style={{ marginBottom: 10 }}>
                        <label className="mkt-field">
                            <span>Имя файла</span>
                            <input
                                value={item.filename || ''}
                                onChange={(e) => updateFile(index, 'filename', e.target.value)}
                                placeholder="googleabc123.html"
                            />
                        </label>
                        <label className="mkt-field" style={{ gridColumn: 'span 2' }}>
                            <span>Содержимое</span>
                            <textarea
                                rows={2}
                                value={item.content || ''}
                                onChange={(e) => updateFile(index, 'content', e.target.value)}
                                placeholder="google-site-verification: googleabc123.html"
                            />
                        </label>
                        <div className="mkt-actions" style={{ marginTop: 22 }}>
                            <button type="button" onClick={() => removeFile(index)}>Удалить</button>
                        </div>
                    </div>
                ))}

                <div className="mkt-actions">
                    <button type="button" onClick={addFile}>Добавить файл</button>
                </div>
            </div>

            <div className="mkt-card">
                <div className="mkt-card__title">Полезные адреса</div>
                <table className="mkt-table">
                    <tbody>
                        <tr>
                            <td>Карта сайта (блог)</td>
                            <td className="mkt-code">{data?.sitemap_url || '—'}</td>
                        </tr>
                        <tr>
                            <td>robots.txt</td>
                            <td className="mkt-code">{data?.robots_url || '—'}</td>
                        </tr>
                        <tr>
                            <td>Файлы подтверждения</td>
                            <td className="mkt-code">
                                {(data?.verification_urls || []).length > 0
                                    ? data.verification_urls.join(', ')
                                    : '—'}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="mkt-actions">
                <button type="button" onClick={save} disabled={loading}>Сохранить</button>
                {message && (
                    <span className={`mkt-message mkt-message--${message.type === 'error' ? 'error' : 'ok'}`}>
                        {message.text}
                    </span>
                )}
            </div>
        </div>
    );
}

export default AdminMarketingIntegrations;
